// src/hooks/useAuth.js
import { useContext, useState, useEffect } from 'react';
import AuthContext from '../context/AuthContext.jsx';

const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }

  const { user, login, logout, loading, isAuthenticated } = context;
  
  // Состояние для ролей (чтобы не вычислять каждый раз)
  const [roleNames, setRoleNames] = useState(new Set());
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [canManageUsers, setCanManageUsers] = useState(false);
  const [isAnalyticsView, setIsAnalyticsView] = useState(false);
  const [userRoles, setUserRoles] = useState([]);

  // Обновляем состояния при изменении user
  useEffect(() => {
    if (user) {
      // Создаем копию ролей, чтобы не мутировать исходный объект
      let rolesCopy = [...(user.roles || [])];
      
      // Добавляем роль superuser в список ролей, если это суперпользователь
      if (user.superuser === true && !rolesCopy.some(r => r.name === 'superuser')) {
        rolesCopy.push({ id: -1, name: 'superuser' });
      }
      
      setUserRoles(rolesCopy);
      
      const rolesSet = new Set(rolesCopy.map(r => r.name));
      setRoleNames(rolesSet);
      
      const admin = rolesSet.has('admin');
      setIsAdmin(admin);
      
      const superuser = user.superuser === true;
      setIsSuperuser(superuser);
      
      setCanManageUsers(superuser || admin);
      
      const analyticsView = !['customer', 'operator', 'view_only'].some(role => rolesSet.has(role));
      setIsAnalyticsView(analyticsView);
    } else {
      setRoleNames(new Set());
      setIsAdmin(false);
      setIsSuperuser(false);
      setCanManageUsers(false);
      setIsAnalyticsView(false);
      setUserRoles([]);
    }
  }, [user]);

  return {
    user,
    login,
    logout,
    loading,
    isAuthenticated,
    isAdmin,
    isSuperuser,
    isAnalyticsView,
    canManageUsers,
    roleNames,
    username: user?.username,
    name: user?.name,
    surname: user?.surname,
    fullName: user ? `${user.name || ''} ${user.surname || ''}`.trim() || user.username : '',
    projects: user?.projects || [],
    roles: userRoles,
  };
};

export default useAuth;
